/**
 * /leads - the Lead Desk: four tabs (Leads, Calendar, Campaigns, Settings). ?tab=<tab> picks a tab and ?lead=<id> opens a lead
 * (emails link straight to it). The old eight-tab page stays at /leads/classic for two weeks as a fallback; reports are at
 * /leads/reports.
 */
import { lazy, Suspense, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Calendar, Download, Loader2, Megaphone, MessageSquare, Settings, Users, UserPlus } from "lucide-react";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import LeadsTab from "./LeadsTab";
import LeadPanel from "./LeadPanel";
import { leadsKey, sendJson } from "./api";

const AppointmentsTab = lazy(() => import("@/pages/lead-desk/AppointmentsTab"));
const CampaignsTab = lazy(() => import("@/pages/leads/CampaignsTab"));
const LeadsSettingsTab = lazy(() => import("./LeadsSettingsTab"));

type Tab = "leads" | "calendar" | "campaigns" | "settings";
const TABS: Array<{ id: Tab; label: string; icon: any }> = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
  { id: "settings", label: "Settings", icon: Settings },
];
const isTab = (v: string | null): v is Tab => !!v && TABS.some((t) => t.id === v);

const Fallback = () => <div className="flex justify-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>;

export default function LeadsPage() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = new URLSearchParams(search);
  const tab: Tab = isTab(params.get("tab")) ? (params.get("tab") as Tab) : "leads";
  const leadParam = Number(params.get("lead"));
  const openLeadId = Number.isInteger(leadParam) && leadParam > 0 ? leadParam : null;
  const [addOpen, setAddOpen] = useState(false);

  const go = (next: { tab?: Tab; lead?: number | null }) => {
    const p = new URLSearchParams(search);
    const t = next.tab ?? tab;
    if (t === "leads") p.delete("tab"); else p.set("tab", t);
    if (next.lead !== undefined) { if (next.lead == null) p.delete("lead"); else p.set("lead", String(next.lead)); }
    const qs = p.toString();
    setLocation(`/leads${qs ? `?${qs}` : ""}`);
  };

  const { data: roleData } = useQuery<{ role: string }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "my-role"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/my-role`, { credentials: "include" });
      if (!res.ok) return { role: "viewer" };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });
  const role = roleData?.role ?? "viewer";
  const canManage = role === "owner" || role === "admin";

  const { data: workspace } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, { credentials: "include" });
      return res.ok ? res.json() : null;
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <MessageSquare size={48} className="text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-display font-bold text-muted-foreground">No workspace selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Pick a workspace to see its leads.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-desk-v2">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Leads</h1>
          <p className="text-muted-foreground mt-1 text-sm">Rogers follows up. You handle what needs you.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" asChild data-testid="link-lead-reports"><Link href="/leads/reports"><BarChart3 /> Reports</Link></Button>
          {canManage && (
            <Button variant="outline" size="sm" asChild data-testid="button-export-csv">
              <a href={`/api/workspaces/${activeWorkspaceId}/leads/export/csv`} download="leads.csv"><Download /> Export CSV</a>
            </Button>
          )}
          {role !== "viewer" && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="button-add-lead"><UserPlus /> Add lead</Button>}
        </div>
      </div>

      <div className="flex border-b border-border overflow-x-auto" role="tablist" data-testid="lead-desk-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => go({ tab: t.id })}
            className={cn(
              "flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors",
              tab === t.id ? "border-blue-500 text-blue-400" : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {tab === "leads" && <LeadsTab workspaceId={activeWorkspaceId} onOpenLead={(id) => go({ lead: id })} />}
      {tab === "calendar" && (
        <Suspense fallback={<Fallback />}>
          <AppointmentsTab workspaceId={activeWorkspaceId} bookingSlug={workspace?.bookingSlug} />
        </Suspense>
      )}
      {tab === "campaigns" && (
        <Suspense fallback={<Fallback />}>
          <CampaignsTab workspaceId={activeWorkspaceId} />
        </Suspense>
      )}
      {tab === "settings" && (
        <Suspense fallback={<Fallback />}>
          <LeadsSettingsTab workspaceId={activeWorkspaceId} isViewer={role === "viewer"} />
        </Suspense>
      )}

      <p className="text-xs text-muted-foreground" data-testid="classic-link-note">
        Looking for the old Lead Desk? <Link href="/leads/classic" className="text-blue-400 hover:underline" data-testid="link-classic-lead-desk">Open the classic view</Link> (available for two more weeks).
      </p>

      <LeadPanel workspaceId={activeWorkspaceId} leadId={openLeadId} onClose={() => go({ lead: null })} canDelete={canManage} />
      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} workspaceId={activeWorkspaceId} onCreated={(id) => go({ tab: "leads", lead: id })} />
    </div>
  );
}

function AddLeadDialog({ open, onOpenChange, workspaceId, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: number; onCreated: (id: number) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const create = useMutation({
    mutationFn: () => sendJson<{ id: number }>("POST", `/api/workspaces/${workspaceId}/leads`, {
      name: name.trim(), email: email.trim() || null, phone: phone.trim() || null, source: "manual",
    }),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: leadsKey(workspaceId) });
      toast({ title: "Lead added" });
      setName(""); setEmail(""); setPhone("");
      onOpenChange(false);
      if (lead?.id) onCreated(lead.id);
    },
    onError: (e: any) => toast({ title: "Couldn't add the lead", description: e?.message, variant: "destructive" }),
  });
  const emailOk = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-add-lead">
        <DialogHeader>
          <DialogTitle>Add a lead</DialogTitle>
          <DialogDescription>Someone who called, walked in or was referred.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="input-add-lead-name" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" data-testid="input-add-lead-email" />
          {!emailOk && <p className="text-xs text-red-400">That email doesn't look right.</p>}
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" type="tel" data-testid="input-add-lead-phone" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-add-lead-cancel">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!name.trim() || !emailOk || create.isPending} data-testid="button-add-lead-save">
            {create.isPending && <Loader2 className="animate-spin" />} Add lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
